import { create } from "zustand";
import type { TokenPair, User } from "@/types";
import { authApi, type RegisterPayload } from "@/api/endpoints";
import { tokenStore } from "@/api/client";

interface AuthState {
  user: User | null;
  status: "idle" | "loading" | "authenticated" | "unauthenticated";
  login: (identifier: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  loginWithGoogle: (accessToken: string) => Promise<void>;
  loginWithFacebook: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setUser: (user: User) => void;
}

// Shared by every login-ish flow below: persist the new tokens, then load the
// user profile to confirm they actually work. If that profile fetch fails
// (network blip, transient 500), clear the tokens again rather than leaving
// a "valid" token sitting in localStorage that a later bootstrap() would
// silently log the user in with, even though login appeared to fail.
async function hydrateSession(
  data: TokenPair,
  set: (partial: Partial<AuthState>) => void,
) {
  tokenStore.set(data);
  try {
    const me = await authApi.me();
    set({ user: me.data, status: "authenticated" });
  } catch (err) {
    tokenStore.clear();
    throw err;
  }
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: "idle",

  setUser: (user) => set({ user, status: "authenticated" }),

  login: async (identifier, password) => {
    const { data } = await authApi.login(identifier, password);
    await hydrateSession(data, set);
  },

  register: async (payload) => {
    const { data } = await authApi.register(payload);
    await hydrateSession(data, set);
  },

  loginWithGoogle: async (accessToken) => {
    const { data } = await authApi.google(accessToken);
    await hydrateSession(data, set);
  },

  loginWithFacebook: async (accessToken) => {
    const { data } = await authApi.facebook(accessToken);
    await hydrateSession(data, set);
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore network errors on logout */
    }
    tokenStore.clear();
    set({ user: null, status: "unauthenticated" });
  },

  // Called once on app load: if a token exists, hydrate the user.
  bootstrap: async () => {
    if (!tokenStore.access) {
      set({ status: "unauthenticated" });
      return;
    }
    set({ status: "loading" });
    try {
      const me = await authApi.me();
      set({ user: me.data, status: "authenticated" });
    } catch {
      tokenStore.clear();
      set({ user: null, status: "unauthenticated" });
    }
  },
}));
